/**
 * TanStack Query hooks for the admin changesets surface — PIM Wave P5 Task
 * 7. Mirrors the `attribute-fields/hooks.ts` idiom (query-key factory,
 * invalidate-on-success mutations).
 *
 * Invalidation rules:
 *  - Any mutation that can move a changeset between status buckets (create,
 *    publish, rollback) or change its `op_count` (append/remove op) OR
 *    delete it entirely invalidates the WHOLE `["admin","changesets","list"]`
 *    prefix — the list is small and admin-curated (no pagination to
 *    preserve), so a blanket prefix-invalidate is simpler and cheaper than
 *    tracking which of the 4 status-filtered cache entries (all/draft/
 *    published/rolled_back) a given transition actually affects.
 *  - Every mutation EXCEPT delete also invalidates that changeset's own
 *    `detail(id)` so the open drawer reflects the new op list / status /
 *    impact_snapshot immediately.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  appendChangesetOperation,
  changesetKeys,
  createChangeset,
  deleteChangeset,
  getChangeset,
  listChangesets,
  previewChangeset,
  publishChangeset,
  removeChangesetOperation,
  rollbackChangeset,
  type ChangesetCreateBody,
  type ChangesetListResponse,
  type ChangesetResponse,
  type ChangesetStatusValue,
  type OpInBody,
} from "@/features/admin/changesets/api";

export function useChangesets(
  status: ChangesetStatusValue | null = null,
): UseQueryResult<ChangesetListResponse, Error> {
  return useQuery<ChangesetListResponse, Error>({
    queryKey: changesetKeys.list(status),
    queryFn: () => listChangesets({ status }),
    staleTime: 10_000,
    // The `pim.changesets` flag being OFF makes every route 404
    // (`pim.changeset.disabled`) — a deterministic outcome, not a transient
    // failure. Without this, the page + all 4 mounted banners each retry the
    // 404 3× (react-query default) before settling the disabled/hidden state.
    retry: false,
  });
}

export function useChangeset(
  id: string | null,
): UseQueryResult<ChangesetResponse, Error> {
  return useQuery<ChangesetResponse, Error>({
    queryKey: changesetKeys.detail(id ?? ""),
    queryFn: () => getChangeset(id!),
    enabled: id !== null,
    // Never mask a fresh mutation behind a stale-time window — the drawer is
    // the source of truth for "what did my last action just do".
    staleTime: 0,
  });
}

function useInvalidateChangesetList(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ["admin", "changesets", "list"] });
  };
}

function useInvalidateChangesetDetail(): (id: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id: string) => {
    await qc.invalidateQueries({ queryKey: changesetKeys.detail(id) });
  };
}

export function useCreateChangeset(): UseMutationResult<
  ChangesetResponse,
  Error,
  ChangesetCreateBody
> {
  const invalidateList = useInvalidateChangesetList();
  return useMutation<ChangesetResponse, Error, ChangesetCreateBody>({
    mutationFn: (body) => createChangeset(body),
    onSuccess: () => invalidateList(),
  });
}

export function useAppendChangesetOperation(): UseMutationResult<
  ChangesetResponse,
  Error,
  { id: string; body: OpInBody }
> {
  const invalidateList = useInvalidateChangesetList();
  const invalidateDetail = useInvalidateChangesetDetail();
  return useMutation<ChangesetResponse, Error, { id: string; body: OpInBody }>({
    mutationFn: ({ id, body }) => appendChangesetOperation(id, body),
    onSuccess: async (_data, { id }) => {
      await Promise.all([invalidateList(), invalidateDetail(id)]);
    },
  });
}

export function useRemoveChangesetOperation(): UseMutationResult<
  ChangesetResponse,
  Error,
  { id: string; index: number }
> {
  const invalidateList = useInvalidateChangesetList();
  const invalidateDetail = useInvalidateChangesetDetail();
  return useMutation<ChangesetResponse, Error, { id: string; index: number }>({
    mutationFn: ({ id, index }) => removeChangesetOperation(id, index),
    onSuccess: async (_data, { id }) => {
      await Promise.all([invalidateList(), invalidateDetail(id)]);
    },
  });
}

export function useDeleteChangeset(): UseMutationResult<void, Error, string> {
  const invalidateList = useInvalidateChangesetList();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteChangeset(id),
    onSuccess: () => invalidateList(),
  });
}

export function usePreviewChangeset(): UseMutationResult<
  ChangesetResponse,
  Error,
  string
> {
  const invalidateDetail = useInvalidateChangesetDetail();
  return useMutation<ChangesetResponse, Error, string>({
    mutationFn: (id) => previewChangeset(id),
    onSuccess: (_data, id) => invalidateDetail(id),
  });
}

export function usePublishChangeset(): UseMutationResult<
  ChangesetResponse,
  Error,
  string
> {
  const invalidateList = useInvalidateChangesetList();
  const invalidateDetail = useInvalidateChangesetDetail();
  return useMutation<ChangesetResponse, Error, string>({
    mutationFn: (id) => publishChangeset(id),
    onSuccess: async (_data, id) => {
      await Promise.all([invalidateList(), invalidateDetail(id)]);
    },
  });
}

export function useRollbackChangeset(): UseMutationResult<
  ChangesetResponse,
  Error,
  string
> {
  const invalidateList = useInvalidateChangesetList();
  const invalidateDetail = useInvalidateChangesetDetail();
  return useMutation<ChangesetResponse, Error, string>({
    mutationFn: (id) => rollbackChangeset(id),
    onSuccess: async (_data, id) => {
      await Promise.all([invalidateList(), invalidateDetail(id)]);
    },
  });
}
